<template>
    <div 
      v-if="isOpen" 
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeDialog"
    >
      <!-- Dialog Content -->
      <div class="max-w-md w-full bg-gray-900 rounded-lg p-8 shadow-xl animate-fade-in">
        <!-- Close Button -->
        <button 
          @click="closeDialog" 
          class="absolute top-4 right-4 text-gray-400"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
        
        <!-- App Logo -->
        <div class="flex justify-center mb-6">
          <img src="/icon.png" alt="Blop It Logo" class="h-16 w-16 rounded-xl" />
        </div>
        
        <!-- Error Alert -->
        <div v-if="error" class="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-sm">
          {{ error }}
        </div>
        
        <!-- Title -->
        <h1 class="text-2xl font-bold text-center mb-2">Login</h1>
        <p class="text-gray-300 text-center mb-8">turn your images into IRL experience.</p>
        
        <!-- Google Sign In Button -->
        <button 
          type="button"
          class="w-full flex items-center justify-center gap-3 bg-gray-800 hover:bg-gray-700 rounded-lg p-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleGoogleLogin"
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" class="w-5 h-5" />
          <span>Sign in with Google</span>
        </button>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onUnmounted } from 'vue'
  import { useAuth } from '~/composables/useAuth'
  
  const { $pb } = useNuxtApp();
  
  const props = defineProps({
    isOpen: {
      type: Boolean,
      required: true
    }
  })
  
  const emit = defineEmits(['close'])
  
  const { loginWithGoogle } = useAuth()
  const isLoading = ref(false)
  const error = ref('')
  
  const closeDialog = () => {
    emit('close')
    // Remove class to allow scrolling again
    document.body.classList.remove('overflow-hidden')
    // Clear any error messages
    error.value = ''
  }
  
  const handleGoogleLogin = async () => {
    try {
      isLoading.value = true
      error.value = ''
      // Clear any existing auth state
      $pb.authStore.clear();
  
      await loginWithGoogle()
      
      window.location.reload()
    } catch (err) {
      error.value = err.message || 'Failed to sign in with Google'
    } finally {
      isLoading.value = false
    }
  }
  
  // Close dialog with escape key
  onMounted(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && props.isOpen) {
        closeDialog()
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscape)
    })
  })
  
  // Watch for isOpen prop changes to handle body scroll
  watch(() => props.isOpen, (newValue) => {
    if (newValue) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  })
  </script>
  
  <style scoped>
  /* Animation for dialog appearance */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.2s ease-out forwards;
  }
  </style>